import React, { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import Grid from "@mui/material/Grid2";

import {
  DescriptionCard,
  DraggableCard,
  PenPortraitCard,
  TopBrandsCard,
  TopMindsetCard,
  TopSitesCard,
} from "@/components/atoms";
import { DemographicsSection, PenPortraitMap } from "@/components/molecules";
import { useInsightFilters } from "@/context";
import {
  InsightMeta,
  InsightResult,
  LocationResult,
  PenPortrait,
  PenPortraitInsightVariable,
  PenPortraitSection,
} from "@/types";
import { getClientComponent } from "@/utils";

interface PenPortraitViewProps {
  insights: InsightResult[];
  pen_portrait: PenPortrait;
  locations: LocationResult;
  meta?: InsightMeta;
  displayExtra?: boolean;
  editable?: boolean;
  mindsets?: PenPortraitInsightVariable[];
  sites?: PenPortraitInsightVariable[];
}

export const PenPortraitView: React.FC<PenPortraitViewProps> = ({
  meta,
  insights,
  pen_portrait,
  locations,
  displayExtra = false,
  editable = true,
  mindsets,
  sites
}) => {
  const {
    filters: { scoreType, levels },
  } = useInsightFilters();

  const [topSections, setTopSections] = useState<PenPortraitSection[]>([]);
  const [bottomSections, setBottomSections] = useState<PenPortraitSection[]>([]);

  useEffect(() => {

    // If sites exist then we need to split the data into top and bottom sections
    if (sites) {
      if (mindsets) {
        // Add mindset section to the start
        setTopSections([
          { title: "mindset", id: 99, score: 0 },
          ...pen_portrait.sections.slice(0,5),
        ]);
        setBottomSections([...pen_portrait.sections.slice(5)]);
      } else {
        setTopSections([...pen_portrait.sections.slice(0,6)]);
        setBottomSections([...pen_portrait.sections.slice(6)]);
      }
    } else {
      // Otherwise everything just goes in the top section

      // Add mindset section to the start
      if (mindsets) {
        setTopSections([
          { title: "mindset", id: 99, score: 0 },
          ...pen_portrait.sections,
        ]);
      } else {
        setTopSections([...pen_portrait.sections]);
      }
    }

  }, [pen_portrait, mindsets, sites]);

  const handleMoveCard = (dragSection: string, hoverSection: string, dragIndex: number, hoverIndex: number) => {

    const updatedTop = [...topSections];
    const updatedBottom = [...bottomSections];

    const sourceArray = dragSection === 'top' ? updatedTop : updatedBottom;
    const targetArray = hoverSection === 'top' ? updatedTop : updatedBottom;

    // Same section swap
    if (dragSection === hoverSection) {
      [sourceArray[dragIndex], sourceArray[hoverIndex]] = [
        sourceArray[hoverIndex],
        sourceArray[dragIndex],
      ];
    } else {
      // Different section swap
      const draggedCard = sourceArray[dragIndex];
      const hoveredCard = targetArray[hoverIndex];

      if (!draggedCard || !hoveredCard) return;

      // Swap the elements at their respective indexes across the arrays
      sourceArray[dragIndex] = hoveredCard;
      targetArray[hoverIndex] = draggedCard;
    }

    setTopSections(updatedTop);
    setBottomSections(updatedBottom);

  };

  const handleRemoveSection = (index: number) => {
    const updatedSections = topSections.filter((_, i) => i !== index);
    setTopSections(updatedSections);
  };

  const ClientDemographicsSection = getClientComponent(
    "DemographicsSection",
    DemographicsSection
  );

  return (
    <Grid container size={12} spacing={2}>
      <Grid container size={12} justifyContent="space-between">
        <Grid container size={8}>
          <ClientDemographicsSection
            insights={insights}
            score={scoreType.value}
          />
          {displayExtra && (
            <Grid size={12}>
              <TopBrandsCard
                variables={pen_portrait.top_scores}
                score={scoreType.value}
                levels={levels}
              />
            </Grid>
          )}
          {meta && (
            <Grid size={12}>
              <DescriptionCard
                description={meta.description_short}
                editable={editable}
              />
            </Grid>
          )}
        </Grid>
        <Grid size={4}>
          <PenPortraitMap scores={locations} extra={displayExtra} />
        </Grid>
      </Grid>

      <DndProvider backend={HTML5Backend}>
        <Grid container size={sites ? 8 : 12} spacing={2}>

          {topSections.map((section, index) => {
            if (section.title === 'mindset' && mindsets) {
              return (
                <Grid size={sites ? 6 : 4} key={`pen-portrait-mindsets`}>
                  <DraggableCard
                    section='top'
                    index={index}
                    onMoveCard={handleMoveCard}
                    type="card"
                  >
                    <TopMindsetCard
                      title={'What are their mindsets?'}
                      variables={mindsets}
                      score={scoreType.value}
                      onRemoveSection={() => handleRemoveSection(index)}
                      editable={editable}
                    />
                  </DraggableCard>
                </Grid>
              );
            } else {
              const variables = pen_portrait.insight.find(
                (v) => v.section === section.title
              )?.variables;
              if (variables) {
                return (
                  <Grid size={sites ? 6 : 4} key={`pen-portrait-${index}`}>
                    <DraggableCard
                      section='top'
                      index={index}
                      onMoveCard={handleMoveCard}
                      type="card"
                    >
                      <PenPortraitCard
                        title={section.title}
                        variables={variables}
                        score={scoreType.value}
                        levels={levels}
                        onRemoveSection={() => handleRemoveSection(index)}
                        editable={editable}
                      />
                    </DraggableCard>
                  </Grid>
                );
              } else {
                return null
              }
            }
          })}

        </Grid>
        {sites && (
          <Grid size={4}>
            <TopSitesCard
              title="What Sites do they visit?"
              variables={sites}
              score={scoreType.value}
            />
          </Grid>
        )}
        <Grid container size={12} spacing={2}>

          {bottomSections.map((section, index) => {
            if (section.title === 'mindset' && mindsets) {
              return (
                <Grid size={4} key={`pen-portrait-mindsets`}>
                  <DraggableCard
                    section='bottom'
                    index={index}
                    onMoveCard={handleMoveCard}
                    type="card"
                  >
                    <TopMindsetCard
                      title={'What are their mindsets?'}
                      variables={mindsets}
                      score={scoreType.value}
                      onRemoveSection={() => handleRemoveSection(index)}
                      editable={editable}
                    />
                  </DraggableCard>
                </Grid>
              );
            } else {
              const variables = pen_portrait.insight.find(
                (v) => v.section === section.title
              )?.variables;
              if (variables) {
                return (
                  <Grid size={4} key={`pen-portrait-${index}`}>
                    <DraggableCard
                      section='bottom'
                      index={index}
                      onMoveCard={handleMoveCard}
                      type="card"
                    >
                      <PenPortraitCard
                        title={section.title}
                        variables={variables}
                        score={scoreType.value}
                        levels={levels}
                        onRemoveSection={() => handleRemoveSection(index)}
                        editable={editable}
                      />
                    </DraggableCard>
                  </Grid>
                );
              } else {
                return null
              }
            }
          })}
        </Grid>
      </DndProvider>
    </Grid>
  );
};

export default PenPortraitView;
